import React, { useState, useMemo, useEffect } from 'react';
import { Search, Plus, SortAsc, MessageSquare, Minus, ExpandIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription } from '../ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useComments } from '../../hooks/useComments';
import { CommentItem } from './CommentItem';
import { CommentForm } from './CommentForm';
import { CommentStats } from './CommentStats';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

interface ProductCommentsTabProps {
  productId: number;
}

/**
 * ProductCommentsTab Component
 *
 * Main container for the product comments feature with:
 * - Comment listing with threading support
 * - Search functionality with debouncing
 * - Sort options and filtering
 * - Statistics display
 * - Collapsible discussions
 */
export function ProductCommentsTab({ productId }: ProductCommentsTabProps) {
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [collapsedDiscussions, setCollapsedDiscussions] = useState<Set<number>>(new Set());

  // Get translations
  const { getCommentsText } = useProductDetailPageTranslations();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Single hook to handle both search and sorting
  const {
    data: commentsData,
    isLoading,
    error,
    refetch,
  } = useComments(productId, {
    flat: false, // Always use threaded view
    search: debouncedSearchTerm.trim() || undefined,
    ordering: sortOrder === 'newest' ? '-created_at' : 'created_at',
  });

  // Determine display state
  const isSearchActive = debouncedSearchTerm.trim().length > 0;
  const displayData = commentsData;
  const isSearching = searchTerm !== debouncedSearchTerm;

  // Memoized comment count for performance
  const commentCount = useMemo(() => {
    return displayData?.total_comments || 0;
  }, [displayData]);

  // Handle sort order toggle
  const toggleSortOrder = () => {
    setSortOrder(prev => (prev === 'newest' ? 'oldest' : 'newest'));
  };

  // Handle comment form
  const handleAddComment = () => {
    setShowCommentForm(true);
  };

  const handleCommentSuccess = () => {
    setShowCommentForm(false);
    refetch();
  };

  const handleCommentCancel = () => {
    setShowCommentForm(false);
  };

  // Handle discussion collapse/expand
  const toggleDiscussionCollapse = (commentId: number) => {
    setCollapsedDiscussions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(commentId)) {
        newSet.delete(commentId);
      } else {
        newSet.add(commentId);
      }
      return newSet;
    });
  };

  const collapseAllDiscussions = () => {
    if (displayData?.results) {
      const topLevelIds = displayData.results
        .filter(comment => !comment.parent && comment.replies_count > 0)
        .map(comment => comment.id);
      setCollapsedDiscussions(new Set(topLevelIds));
    }
  };

  const expandAllDiscussions = () => {
    setCollapsedDiscussions(new Set());
  };

  const hasCollapsedDiscussions = collapsedDiscussions.size > 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {getCommentsText('tab.errors.loadFailed', { message: error.message })}
        </AlertDescription>
      </Alert>
    );
  }

  // Empty state
  if (!displayData || commentCount === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageSquare className="size-5" />
            <h2 className="text-lg font-semibold">{getCommentsText('tab.title')}</h2>
            <Badge variant="secondary">0</Badge>
          </div>
          <Button onClick={handleAddComment}>
            <Plus className="mr-2 size-4" />
            {getCommentsText('tab.actions.addComment')}
          </Button>
        </div>

        {/* Empty State */}
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="mb-4 size-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium">
              {getCommentsText('tab.emptyState.noComments')}
            </h3>
            <p className="mb-6 text-center text-muted-foreground">
              {isSearchActive
                ? getCommentsText('tab.emptyState.noSearchResults', {
                    searchTerm: debouncedSearchTerm,
                  })
                : getCommentsText('tab.emptyState.beFirstToComment')}
            </p>
            <Button onClick={handleAddComment}>
              <Plus className="mr-2 size-4" />
              {getCommentsText('tab.actions.addFirstComment')}
            </Button>
          </CardContent>
        </Card>

        {/* Comment Form */}
        {showCommentForm && (
          <Card>
            <CardHeader>
              <CardTitle>{getCommentsText('tab.form.addTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentForm
                productId={productId}
                onSuccess={handleCommentSuccess}
                onCancel={handleCommentCancel}
              />
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="size-5" />
          <h2 className="text-lg font-semibold">{getCommentsText('tab.title')}</h2>
          <Badge variant="secondary">{commentCount}</Badge>
          {isSearchActive && (
            <Badge variant="outline" className="text-xs">
              {getCommentsText('tab.header.found', {
                count: commentCount,
                searchTerm: debouncedSearchTerm,
              })}
            </Badge>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Collapse/Expand All Button */}
          {displayData?.results &&
            displayData.results.some(c => !c.parent && c.replies_count > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={hasCollapsedDiscussions ? expandAllDiscussions : collapseAllDiscussions}
              >
                {hasCollapsedDiscussions ? (
                  <>
                    <ExpandIcon className="mr-2 size-4" />
                    {getCommentsText('tab.actions.expandAll')}
                  </>
                ) : (
                  <>
                    <Minus className="mr-2 size-4" />
                    {getCommentsText('tab.actions.collapseAll')}
                  </>
                )}
              </Button>
            )}

          {/* Sort Toggle */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <SortAsc className="mr-2 size-4" />
                {sortOrder === 'newest'
                  ? getCommentsText('tab.actions.newest')
                  : getCommentsText('tab.actions.oldest')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setSortOrder('newest')}>
                {getCommentsText('tab.actions.newest')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder('oldest')}>
                {getCommentsText('tab.actions.oldest')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button onClick={handleAddComment}>
            <Plus className="mr-2 size-4" />
            {getCommentsText('tab.actions.addComment')}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={getCommentsText('tab.header.searchPlaceholder')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-10"
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="size-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}
      </div>

      {/* Statistics */}
      <CommentStats data={displayData} />

      {/* Comment Form */}
      {showCommentForm && (
        <Card>
          <CardHeader>
            <CardTitle>{getCommentsText('tab.form.addTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <CommentForm
              productId={productId}
              onSuccess={handleCommentSuccess}
              onCancel={handleCommentCancel}
            />
          </CardContent>
        </Card>
      )}

      {/* Comments List */}
      <div className="space-y-4">
        {displayData.results.map(comment => (
          <CommentItem
            key={comment.id}
            comment={comment}
            productId={productId}
            showReplies={true} // Always show replies since we're always in threaded mode
            isCollapsed={collapsedDiscussions.has(comment.id)}
            onToggleCollapse={
              comment.parent === null && comment.replies_count > 0
                ? () => toggleDiscussionCollapse(comment.id)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}
